import { useMutation } from '@apollo/client';
import moment from 'moment-timezone';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { UserAvatar } from 'modules/workflows/components/UserAvatar';
import { getWorkflowState } from 'modules/workflows/ducks';
import { parseForInsertPreview } from 'modules/workflows/utils';
import { Delete24 } from 'ui/icons';
import { Body2 } from 'ui/theme';
import { BUTTON_SIZES, SecondaryDestructiveButton, Button, BUTTON_VARIANTS } from 'ui/button';
import { displayDate } from 'utils/date';
import { TilesetPreview } from './TilesetPreview';
import { CONTENT } from './constants';
import {
  CreatedBy,
  CreatedByUser,
  DeleteTileset,
  RightSideContainer,
  TilesetDescription,
  TilesetDetail,
  TilesetDetails,
  TilesetTitle,
} from './styles';
import { deleteTilesetMutation } from './tilesets.graphql';
import { Tileset, TilesetData } from './types';

export type TilesetModalProps = {
  tileset?: Tileset;
  onDeleteTileset: () => void;
};

export const TILESET_NAME_ID = 'tileset-name';
export const IMPORT_COUNT_ID = 'import-count';
export const CREATED_BY_ID = 'created-by';
export const DELETE_CONFIRMATION_ID = 'delete-confirmation';
export const DELETE_TILESET_ID = 'delete-tileset';

export const SelectedTileset = ({ tileset, onDeleteTileset }: TilesetModalProps) => {
  const dispatch = useDispatch();
  const wfState = useSelector(getWorkflowState);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [deleteTileset, { loading: deletingTileset }] = useMutation(deleteTilesetMutation, {
    onCompleted: () => {
      onDeleteTileset();
      dispatch(addPositiveToast(CONTENT.deleteSuccessToast));
      setShowConfirmation(false);
    },
    onError: () => {
      dispatch(addNegativeToastWithErrorId(CONTENT.deleteFailToast, 'deleteTileset'));
    },
  });

  const { nodes = [], edges = [], notes = [] }: TilesetData = tileset?.data || ({} as any);

  const { parsedNodesForDisplay, parsedEdgesForDisplay } = useMemo(
    () => parseForInsertPreview({ nodes, edges, notes }, wfState),
    [nodes, edges, notes],
  );

  const getDisplayDate = ({ createdAt }: Pick<Tileset, 'createdAt'>) => {
    const timestamp = createdAt?.timestamp?.split('[')[0];
    const formattedDisplayDate = displayDate(timestamp, 'MMM D YYYY');
    const formattedTimeZone = moment(timestamp).tz(createdAt?.timezone).format('hh:mma z');

    return `${formattedDisplayDate}, at ${formattedTimeZone}`;
  };

  return (
    <RightSideContainer>
      {tileset && (
        <div data-test={TILESET_NAME_ID}>
          <TilesetTitle>{tileset.name}</TilesetTitle>
          <TilesetDescription>{tileset.description}</TilesetDescription>
        </div>
      )}

      <TilesetPreview
        nodes={parsedNodesForDisplay}
        edges={parsedEdgesForDisplay}
        isLoading={!tileset}
      />

      {tileset && (
        <>
          <TilesetDetails>
            <TilesetDetail>
              {CONTENT.inserted}
              <Body2 data-test={IMPORT_COUNT_ID}>{`${tileset.importCount} ${
                tileset.importCount === 1
                  ? CONTENT.insertedCountSingle
                  : CONTENT.insertedCountPlural
              }`}</Body2>
            </TilesetDetail>
            <TilesetDetail>
              {CONTENT.created}
              <CreatedBy>
                <Body2>{getDisplayDate(tileset)}</Body2>

                <CreatedByUser data-test={CREATED_BY_ID}>
                  {tileset?.createdBy && (
                    <UserAvatar
                      userId={tileset?.createdBy}
                      showFullName
                      showFullNameTooltip={false}
                    />
                  )}
                </CreatedByUser>
              </CreatedBy>
            </TilesetDetail>
          </TilesetDetails>

          <DeleteTileset>
            {!showConfirmation ? (
              <Button
                size={BUTTON_SIZES.LARGE}
                variant={BUTTON_VARIANTS.GHOST}
                onClick={() => setShowConfirmation(true)}
                data-test={DELETE_CONFIRMATION_ID}
              >
                <Delete24 />
                {CONTENT.deleteButton}
              </Button>
            ) : (
              <>
                Are you sure you want to delete this tileset?
                <SecondaryDestructiveButton
                  size={BUTTON_SIZES.LARGE}
                  onClick={() => deleteTileset({ variables: { id: tileset?.id } })}
                  data-test={DELETE_TILESET_ID}
                  isLoading={deletingTileset}
                >
                  <Delete24 />
                  {CONTENT.deleteButton}
                </SecondaryDestructiveButton>
                <Button
                  size={BUTTON_SIZES.LARGE}
                  onClick={() => setShowConfirmation(false)}
                  variant={BUTTON_VARIANTS.GHOST}
                >
                  {CONTENT.cancelButton}
                </Button>
              </>
            )}
          </DeleteTileset>
        </>
      )}
    </RightSideContainer>
  );
};
